import React, { useRef, useCallback, useMemo } from 'react';

import { EditorConfiguration as CmOptions } from 'codemirror';
import { useSelector } from 'react-redux';

import { getGlobalTabSize } from 'src/redux/diff-settings';

import { handleEditorKeydown } from './cm-key-handler';
import {
  AddedCodemirror,
  RemovedCodemirror,
} from './codemirror-diff-editor-lite-styled';
import { registerEditor, unregisterEditor } from './codemirrorRegistry';
import { getEditorView } from './editorViewRegistry';

interface Props {
  onChange: (s: string) => void;
  startLineNum: number;
  originalCode: string;
  updatedCode: string;
  nodeLocalId: string;
  disableCMCursor: boolean;
  parentEditorId: string;
}

const CodemirrorDiffEditorLiteInternal = ({
  onChange,
  startLineNum,
  originalCode,
  updatedCode,
  nodeLocalId,
  disableCMCursor,
  parentEditorId,
}: Props): JSX.Element => {
  const editorRef = useRef(null);
  const editorView = useRef(null);
  const tabSize = useSelector(getGlobalTabSize);

  const handleEditorDidMount = useCallback(
    (editor: any) => {
      editorRef.current = editor;

      registerEditor(nodeLocalId, editor);

      // Set focus to the editor on mount
      editor.focus();

      editorView.current = getEditorView(parentEditorId);

      // clear out highlighted text on blur
      editor.on('blur', () => {
        editor.setSelection({ line: 0, ch: 0 }, { line: 0, ch: 0 });
      });
    },
    [nodeLocalId, parentEditorId]
  );

  const handleBeforeChange = useCallback(
    (_1: any, _2: any, newCode: string) => {
      onChange(newCode);
    },
    [onChange]
  );

  const handleKeyDown = useCallback(
    (editor: any, event: any) => {
      handleEditorKeydown({
        event,
        cmEditor: editor,
        pmEditorView: editorView.current,
        nodeLocalId,
      });
    },
    [nodeLocalId]
  );

  const cmOptions = useMemo(
    () => ({
      lineNumbers: true,
      styleSelectedText: true,
      smartIndent: true,
      tabSize,
      firstLineNumber: startLineNum,
      maxHighlightLength: Infinity,
      extraKeys: {
        Tab: (cm: any) => {
          const spaces = ' '.repeat(tabSize);
          cm.replaceSelection(spaces);
        },
        'Shift-Tab': 'indentLess',
      },
    }),
    [startLineNum, tabSize]
  );

  const cmOptionsRemoved: CmOptions = useMemo(
    () => ({ ...cmOptions, readOnly: 'nocursor' }),
    [cmOptions]
  );

  const cmOptionsAdded: CmOptions = useMemo(
    () => ({
      ...cmOptions,
      readOnly: disableCMCursor ? 'nocursor' : false,
    }),
    [cmOptions, disableCMCursor]
  );
  const noop = useCallback(() => null, []);

  return (
    <div>
      {/* This is just used to render the original line being removed. This is
      not editable but we're using a codemirror component so the highlighting and styling
      matches exactly. */}
      <RemovedCodemirror
        className="codemirror-diff-editor-lite--readonly"
        value={originalCode}
        options={cmOptionsRemoved}
        onBeforeChange={noop}
      />
      <AddedCodemirror
        className="codemirror-diff-editor-lite--editable"
        ref={editorRef}
        value={updatedCode}
        options={cmOptionsAdded}
        editorDidMount={handleEditorDidMount}
        onBeforeChange={handleBeforeChange}
        onKeyDown={handleKeyDown}
        editorWillUnmount={() => unregisterEditor(nodeLocalId)}
      />
    </div>
  );
};
export const CodemirrorDiffEditorLite = React.memo(
  CodemirrorDiffEditorLiteInternal
);
